import { useCallback } from 'react'
import { faEdit, faTrashCan } from '@fortawesome/pro-regular-svg-icons'
import { centsToDollars } from '@/utils/currencyHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { getUserTransactionById } from '../transactions.selectors'
import {
  deleteManualTransaction,
  updateUserTransaction,
} from '../transactions.slice'
import { DATE_FORMATS_LUXON, isoToUTCDateTime } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TransactionRuleCategoryType } from '@/reducers/admin/transactionRulesReducer'

import { TransactionNoteModal } from './TransactionNoteModal'
import { Card, CardColors, Icon, Select, Text } from '@/ui'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { TransactionCategoryDropdown } from '@/components/TransactionCategoryDropdown'
import { TextColors, TypographyStyles } from '@/styles'

export const TransactionCard = ({
  transactionId,
  variant = 'default',
  editAction,
  deleteAction,
  followUpYear,
}: {
  transactionId: number
  backgroundColor?: string
  variant?: 'default' | 'edit'
  editAction?: (transactionId: number) => void
  deleteAction?: (transactionId: number) => void
  followUpYear?: string
}) => {
  const dispatch = useAppDispatch()
  const transaction = useReselector(getUserTransactionById, transactionId)

  const updateTransactionCategoryId = useCallback(
    (transactionCategoryId: number | null) =>
      dispatch(
        updateUserTransaction(
          transactionId,
          {
            transactionCategoryId,
            // Auto set the type as business if categorized
            type: transactionCategoryId
              ? TransactionRuleCategoryType.business
              : undefined,
            ...(followUpYear
              ? {
                  requestedClarificationAt: null,
                  notifiedRequestedClarificationAt: null,
                }
              : {}),
          },
          followUpYear
        )
      ),
    [dispatch, followUpYear, transactionId]
  )

  const updateTransactionType = useCallback(
    (type: TransactionRuleCategoryType) => {
      const updateTransactionObject: {
        type: TransactionRuleCategoryType
        transactionCategoryId?: number | null
        requestedClarificationAt?: string | null
        notifiedRequestedClarificationAt?: string | null
      } = {
        type,
        // Clear category if transaction is personal
        transactionCategoryId: type === 'personal' ? null : undefined,
      }
      if (followUpYear) {
        updateTransactionObject.requestedClarificationAt = null
        updateTransactionObject.notifiedRequestedClarificationAt = null
      }

      updateUserTransaction(
        transactionId,
        updateTransactionObject,
        followUpYear
      )(dispatch)
    },
    [dispatch, followUpYear, transactionId]
  )

  const onDelete = useCallback(async () => {
    await deleteManualTransaction(transactionId)(dispatch)
    deleteAction?.(transactionId)
  }, [deleteAction, dispatch, transactionId])

  if (!transaction) {
    return null
  }

  return (
    <Card color={CardColors.LEVEL_0} className="p-6">
      <div className="grid grid-cols-12 items-center gap-4 px-2 py-2 border-b">
        {/* Date */}
        <Text variant={TypographyStyles.BODY_SM} className="col-span-2">
          {isoToUTCDateTime(transaction.date).toFormat(
            DATE_FORMATS_LUXON.DISPLAY_SHORT_YEAR
          )}
        </Text>

        {/* Description */}
        <Text
          variant={TypographyStyles.BODY_SM}
          className="col-span-3 truncate"
          title={transaction.description}
        >
          {transaction.description}
        </Text>

        {/* Amount */}
        <Text variant={TypographyStyles.BODY_SM} className="col-span-1">
          <CurrencyFormatLabel
            value={centsToDollars(transaction.amountInCents)}
          />
        </Text>

        {/* Type Dropdown */}
        <div className="col-span-2">
          <Select
            options={[
              { text: 'Business', value: TransactionRuleCategoryType.business },
              { text: 'Personal', value: TransactionRuleCategoryType.personal },
            ]}
            value={
              transaction.type === null || transaction.type === 'split'
                ? undefined
                : transaction.type
            }
            disabled={transaction.type === 'split'}
            onValueChange={(value) =>
              updateTransactionType(value as TransactionRuleCategoryType)
            }
            variant="text"
            placeholder="Select Type"
          />
        </div>

        {/* Category Dropdown - col span 4 for better contained fit */}
        <div className="col-span-4 overflow-hidden">
          {transaction.type === 'personal' ? (
            <Text>
              <b>N/A</b>
            </Text>
          ) : (
            <TransactionCategoryDropdown
              optionStyle="normal"
              onChange={updateTransactionCategoryId}
              value={transaction.transactionCategoryId || undefined}
              disabled={variant === 'edit'}
              transactionDate={transaction.date}
              // New props to better size the dropdown to fix the space used
              widthClassName="w-full"
              buttonWidthClassName="w-full"
            />
          )}
        </div>

        {/* Icons (edit mode) */}
        {variant === 'edit' && (
          <div className="col-span-12 mt-2 flex justify-end gap-6">
            <Icon icon={faEdit} onClick={() => editAction?.(transaction.id)} />
            <Icon icon={faTrashCan} onClick={onDelete} />
          </div>
        )}

        {/* Note button (default mode) */}
        {variant === 'default' && (
          <div className="col-span-12 mt-2 text-right">
            <TransactionNoteModal
              transaction={transaction}
              followUpYear={followUpYear}
            />
          </div>
        )}
      </div>

      {/* Optional notes row */}
      {transaction.notes && (
        <div className="col-span-12 px-2 pt-1 pb-4">
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            <i>{transaction.notes}</i>
          </Text>
        </div>
      )}
    </Card>
  )
}
